<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { getNotificationsContext } from '@client/components/notifications';

  import { AdsService } from '@client/services/ads';
  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';
  import { activeDomain, internalDomains } from '@client/stores/domain-settings';

  import { isInternalRecipient } from '@client/utils/activity';
  import { consolidateMetrics, formatExportRate } from '@client/utils/banners';
  import { downloadFile } from '@client/utils/csv';

  import { deepCopy } from '@common/utils/objects';
  import { generateUUID } from '@common/utils/strings';

  export let row = {};
  export let props = {};
  export let icon = 'os-icon-more-vertical action-icon';
  export let isEditing = false;

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const onPauseCampaign = async (campaign) => {
    $appStore.loading = true;

    const updatedCampaign = {
      ...campaign,
      paused: true
    };

    const response = await CampaignsService.updateCampaign(updatedCampaign);

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.UPDATE', { values: { rulename: campaign.rulename } }), 'error');

      return;
    }

    notify($format('label.SUCCESS.UPDATE', { values: { item: campaign.rulename } }));

    if (props.update) {
      await props.update(response);
    }
  };

  const onActivateCampaign = async (campaign) => {
    $appStore.loading = true;

    const updatedCampaign = {
      ...campaign,
      paused: false
    };

    const response = await CampaignsService.updateCampaign(updatedCampaign);

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.UPDATE', { values: { rulename: updatedCampaign.rulename } }), 'error');

      return;
    }

    notify($format('label.SUCCESS.UPDATE', { values: { item: updatedCampaign.rulename } }));

    if (props.update) {
      await props.update(response);
    }
  };

  const onCloneCampaign = async (campaign) => {
    $appStore.loading = true;

    const rulename = `${campaign.rulename}_clone_${generateUUID()}`;

    const cloneData = {
      ...deepCopy(campaign),
      rulename,
      _id: null
    };

    const activeTargeting = await TargetingService.get();

    const currentAccountTargeting = activeTargeting.accounts
      .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
      .map(({ name }) => name);

    currentAccountTargeting.forEach((name) => {
      const existingAccount = activeTargeting.accounts.find((account) => account.name === name);

      if (existingAccount) {
        const existingAd = existingAccount.ads.find(({ text }) => text === cloneData.rulename);

        if (!existingAd) {
          existingAccount.ads.push({
            text: cloneData.rulename
          });
        }
      } else {
        activeTargeting.accounts.push({
          name,
          ads: [
            {
              text: cloneData.rulename
            }
          ],
          tags: [],
          isNew: true
        });
      }
    });

    const cloneTargetingResponse = await TargetingService.update(activeTargeting);

    if (cloneTargetingResponse?.status >= 400) {
      notify(
        $format('label.FAILURE.CREATE', {
          values: { type: campaignData.rulename }
        }),
        'error'
      );

      return;
    }

    const cloneCampaignResponse = await CampaignsService.createCampaign(cloneData);

    $appStore.loading = false;

    if (cloneCampaignResponse?.status >= 400) {
      notify($format('label.FAILURE.CREATE', { values: { type: cloneData.rulename } }), 'error');

      return;
    }

    notify($format('label.SUCCESS.CREATE', { values: { type: cloneData.rulename } }));

    navigate(`/marketing/campaigns/${cloneData.rulename}`);

    if (isEditing) {
      location.reload();
    }
  };

  const generateCampaignReport = async (campaign, reportType, serviceMethod) => {
    const campaignStartDate = campaign?.schedule?.startDate ? new Date(campaign.schedule.startDate) : null;
    const today = new Date();
    const daysSinceStart = campaignStartDate ? Math.ceil((today - campaignStartDate) / (1000 * 60 * 60 * 24)) : 0;
    const days = Math.max(daysSinceStart, 730);

    let history = await serviceMethod(campaign._id, days);

    // When "Allow Internal" is off, drop internal recipients so the export matches the
    // performance metrics (which exclude internal events server-side via countinternal).
    if (!campaign.countinternal && Array.isArray(history)) {
      history = history.filter((entry) => !isInternalRecipient(entry.recipient, $activeDomain, $internalDomains));
    }

    const headers = [
      $format('label.SENDER'),
      $format('label.RECIPIENT'),
      $format('label.DATE'),
      $format('label.ID'),
      'ISP'
    ];

    const rows = history.map((entry) => [
      entry.sender,
      entry.recipient,
      new Date(entry.utcts),
      entry.msghashid,
      entry?.note?.isp?.replace(/,/g, ' ') || ''
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(','));

    downloadFile(csvContent.join('\n'), `${campaign.rulename}-${reportType}`);
  };

  const onExportClicks = async (campaign) => {
    await generateCampaignReport(campaign, 'clicks', AdsService.getClickReport);
  };

  const onDownloadDeliveries = async (campaign) => {
    await generateCampaignReport(campaign, 'deliveries', AdsService.getDeliveryReport);
  };

  const onExportBasicMetrics = async (campaign) => {
    $appStore.loading = true;

    const campaignStartDate = campaign?.schedule?.startDate ? new Date(campaign.schedule.startDate) : null;
    const today = new Date();
    const daysSinceStart = campaignStartDate ? Math.ceil((today - campaignStartDate) / (1000 * 60 * 60 * 24)) : 0;
    const days = Math.max(daysSinceStart, 730);

    const data = await CampaignsService.getMetrics({ aid: campaign._id, days });
    const metrics = consolidateMetrics(data, campaign);

    $appStore.loading = false;

    const headers = [
      $format('label.REPORTING_PERIOD'),
      $format('label.CAMPAIGN'),
      $format('label.DAYS_LIVE'),
      $format('label.TYPE'),
      $format('label.TAGS'),
      $format('label.BASIC_VIEWS'),
      $format('label.BASIC_CLICKS'),
      $format('label.BASIC_CLICK_THROUGH_RATE'),
      $format('label.URL')
    ];

    const row = [
      'all-time',
      campaign.rulename,
      metrics.totals?.dayslive || 0,
      campaign.adtype,
      (campaign.tags || []).map((tag) => tag.text).join('; '),
      metrics.totals?.basicadview || 0,
      metrics.totals?.basicadclick || 0,
      formatExportRate(metrics.totals?.basicCtr),
      campaign.previewURL || ''
    ];

    const csvContent = [headers, row].map((e) => e.join(',')).join('\n');

    downloadFile(csvContent, `${campaign.rulename}-basic-metrics-${Date.now()}`);
  };

  const onRenameHandler = () => {
    if (props.onRename) {
      props.onRename(row);
    }
  };

  const onDeleteCampaign = async (campaign) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.CAMPAIGN').toLowerCase() } })
    );

    if (confirmed) {
      $appStore.loading = true;

      const activeTargeting = await TargetingService.get();

      const currentAccountTargeting = activeTargeting.accounts
        .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
        .map(({ name }) => name);

      const updatedAccounts = activeTargeting.accounts.map((account) => {
        if (currentAccountTargeting.includes(account.name)) {
          account.ads = account.ads.filter(({ text }) => text !== campaign.rulename);
        }

        return account;
      });

      const combinedTargeting = {
        accounts: updatedAccounts,
        departments: activeTargeting.departments
      };

      const targetingResponse = await TargetingService.update(combinedTargeting);

      if (targetingResponse?.status >= 400) {
        $appStore.loading = false;
        notify($format('label.FAILURE.DELETE', { values: { type: campaign.rulename } }), 'error');
        return;
      }

      const campaignDeleteResponse = await CampaignsService.deleteCampaign(campaign);

      $appStore.loading = false;

      if (campaignDeleteResponse?.status >= 400) {
        notify($format('label.FAILURE.DELETE', { values: { type: campaign.rulename } }), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE', { values: { type: campaign.rulename } }));

      if (isEditing) {
        navigate('/marketing/campaigns');
      } else {
        if (props.delete) {
          await props.delete(campaign);
        }
      }
    }
  };

  let isOpen = false;

  $: showPaused =
    (row.active || row?.targetingSources) &&
    !row?.paused &&
    !(new Date(row?.schedule?.startDate).getTime() >= new Date().getTime());
</script>

<ButtonDropdown bind:isOpen>
  {#if isEditing}
    <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
  {:else}
    <DropdownToggle color="none" tag={icon === 'os-icon-more-vertical action-icon' ? 'span' : 'div'}>
      <i class={icon} class:active={isOpen} />
    </DropdownToggle>
  {/if}
  <DropdownMenu>
    {#if props.onRename}
      <DropdownItem on:click={onRenameHandler}>{$format('label.RENAME')}</DropdownItem>
      <DropdownItem divider />
    {/if}
    {#if showPaused}
      <DropdownItem on:click={() => onPauseCampaign(row)}>{$format('label.PAUSE')}</DropdownItem>
      <DropdownItem divider />
    {/if}
    {#if row.paused}
      <DropdownItem on:click={() => onActivateCampaign(row)}>{$format('label.RESUME')}</DropdownItem>
      <DropdownItem divider />
    {/if}
    {#if !isEditing && !props.isPerformance}
      <DropdownItem on:click={() => redirect(row.rulename)}>{$format('label.EDIT')}</DropdownItem>
    {:else if !isEditing && props.isPerformance}
      <DropdownItem on:click={() => redirect(`/marketing/campaigns/${row.rulename}`)}
        >{$format('label.EDIT')}</DropdownItem
      >
    {/if}

    {#if !props.isPerformance}
      <DropdownItem on:click={() => onCloneCampaign(row)}>{$format('label.CLONE')}</DropdownItem>
      <DropdownItem divider />
      <DropdownItem on:click={() => onExportClicks(row)}>{$format('label.EXPORT_CLICKS')}</DropdownItem>
      <DropdownItem on:click={() => onDownloadDeliveries(row)}>{$format('label.EXPORT_DELIVERIES')}</DropdownItem>
      <DropdownItem on:click={() => onExportBasicMetrics(row)}>{$format('label.EXPORT_BASIC_METRICS')}</DropdownItem>
      <DropdownItem divider />
      <DropdownItem on:click={() => onDeleteCampaign(row)} disabled={row.active}>{$format('label.DELETE')}</DropdownItem
      >
    {/if}
  </DropdownMenu>
</ButtonDropdown>

<style lang="scss">
  i {
    border: 1px solid transparent;
    border-radius: var(--border-radius-base);
    color: var(--color-text);
    cursor: pointer;
    padding: var(--padding-small) var(--padding-xsmall);

    &.active,
    &:hover {
      border-color: var(--color-gray-200);
    }
  }
</style>
